/**
 * 통합 일정 섹션 — 탭 통합 버전.
 *
 * 이전: 일정 / 출장 일지 / 장애 이력 세 탭 분리. 사용자가 "어느 탭에 무엇이
 * 있더라" 를 매번 떠올려야 했고, 한 화면에서 시간순으로 전체를 훑을 수 없었음.
 *
 * 현재: 세 entity 를 단일 UnifiedItem 으로 normalize 해서 시간순 통합
 *  timeline 으로 노출. 우측 캘린더에 통합 마커. 좌측 timeline 은:
 *   - 정렬 토글: 최신순 / 과거순 / 유형별 그룹
 *   - 유형 필터 chip: 전체 / 일정 / 출장 / 장애 (다중 선택)
 *   - 카드 클릭 → 해당 type 의 detail dialog (편집/삭제 가능)
 *
 * 신규 등록은 상단 '등록' 버튼 하나 → RecordFormDialog. 종류(일정·메모·파일·
 * 출장 일지)는 그 다이얼로그 안에서 고른다. 예전에는 메뉴를 열어 종류를 먼저
 * 고르고 다시 폼에서 확인하는 3단계였다.
 *   - 장애 이력만 예외 — form 이 IncidentListView 안에 inline 으로만 있어 아직
 *     통합 다이얼로그에 넣지 못했다. 사이드바 '장애 이력' 메뉴에서 등록한다.
 *
 * defaultCategory prop 은 사이드바 어느 메뉴에서 들어왔는지에 따른 initial
 * filter 만 결정 — 'schedules' → 전체(통합 메인 진입점), 'journal' → 출장만,
 * 'incidents' → 장애만. 사용자가 chip 클릭으로 즉시 변경 가능.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Pagination } from '../../../components/FileList/Pagination';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowDownward as DescIcon,
  ArrowUpward as AscIcon,
  CalendarMonth as CalendarIcon,
  CategoryOutlined as GroupedIcon,
  Description as JournalIcon,
  Person as PersonIcon,
  Place as PlaceIcon,
  Refresh as RefreshIcon,
  UploadFileOutlined as UploadFileIcon,
  Warning as IncidentIcon,
} from '@mui/icons-material';

import {
  EVENT_TYPE_LABEL,
  SCHEDULE_STATUS_LABEL,
  ScheduleEventType,
  SiteSchedule,
  eventTypeFallback,
} from '../../../models/SiteSchedule';
import { SiteVisitLog } from '../../../models/SiteVisitLog';
import {
  SEVERITY_LABEL,
  STATUS_LABEL as INCIDENT_STATUS_LABEL,
  SiteIncident,
} from '../../../models/SiteIncident';
import { SiteScheduleService } from '../../../services/SiteScheduleService';
import { markdownToPlainText } from '../../../utils/markdown';
import { SiteVisitLogService } from '../../../services/SiteVisitLogService';
import { useSiteTimelineData } from '../../../hooks/useSiteTimelineData';

import MiniCalendar, { CalendarEvent } from './MiniCalendar';
import { isoOf } from '../../SiteSchedulesPage/calendarUtils';
import ScheduleDetailDialog from '../dialogs/ScheduleDetailDialog';
import VisitLogDetailDialog from '../dialogs/VisitLogDetailDialog';
import ScheduleFormDialog, {
  canEditSchedule,
} from '../../../components/dialogs/ScheduleFormDialog';
import VisitLogFormDialog from '../../../components/dialogs/VisitLogFormDialog';
import RecordFormDialog from '../../../components/dialogs/RecordFormDialog';
import QuickRecordFab from '../../../components/QuickRecordFab';
import { getUserInfo } from '../../../utils/auth';
import { RECORD_KIND_META, RecordKind } from '../../../components/dialogs/RecordKindSelector';

type UnifiedKind = 'schedule' | 'visit' | 'incident';
type SortMode = 'desc' | 'asc' | 'grouped';

const TIMELINE_PAGE_SIZE = 5;
type Category = 'schedules' | 'journal' | 'incidents';

interface Props {
  projectId: number;
  /** 현재 현장명 — 일정 등록 모달에 고정 현장 필드로 표시. */
  siteName?: string;
  /** 사이드바에서 어느 카테고리로 들어왔는지. initial filter 로만 사용. */
  defaultCategory?: Category;
}

interface UnifiedItem {
  kind: UnifiedKind;
  /** entity id (kind 와 함께 React key 로 사용). */
  id: number;
  /**
   * ISO 8601, 또는 null — **시점 없는 메모**. null 이면 캘린더 마커를 찍지 않고
   * 타임라인에서는 날짜 있는 항목보다 뒤로 밀린다.
   */
  at: string | null;
  title: string;
  description?: string;
  /** 부가 정보 chip — kind 별로 다른 의미 (event_type / severity / "기록"). */
  meta?: string;
  /** 상태 chip — schedule.status / incident.status. visit 은 항상 '기록'. */
  statusLabel?: string;
  /** 담당자 표시명 — assignee / visitor_name / assignee. */
  who?: string;
  /** kind 별 색상 — 카드 좌측 strip / 캘린더 dot. */
  color: 'primary' | 'info' | 'warning' | 'error' | 'secondary' | 'success' | 'default';
  /** original entity — detail dialog 호출 시 그대로 전달. */
  raw: SiteSchedule | SiteVisitLog | SiteIncident;
}

// 출장 일지(초록)/장애 이력(빨강)은 kind 고정색, '일정'은 event_type
// (출장/점검/장애/미팅/기타)별 색으로 구분한다 — 메인 대시보드 일정 캘린더의
// EVENT_COLOR 팔레트와 같은 의미 체계(출장 파랑/점검 하늘/장애 빨강/미팅 보라).
const KIND_META: Record<
  UnifiedKind,
  { label: string; Icon: typeof CalendarIcon; color: UnifiedItem['color'] }
> = {
  schedule: { label: '일정', Icon: CalendarIcon, color: 'primary' },
  visit: { label: '출장 일지', Icon: JournalIcon, color: 'success' },
  incident: { label: '장애 이력', Icon: IncidentIcon, color: 'error' },
};

// '일정' 항목의 event_type 별 색 — 카드 좌측 strip / 캘린더 dot / 범례에 사용.
const SCHEDULE_TYPE_COLOR: Record<ScheduleEventType, UnifiedItem['color']> = {
  visit: 'primary',
  maintenance: 'info',
  incident: 'error',
  meeting: 'secondary',
  worklog: 'warning',
  // 'success' 는 출장 일지 kind 색과 같지만, 메모/기타를 같은 회색으로 두면
  // '일정' 가족 안에서 구분이 사라진다 — 유형 chip 라벨이 둘을 갈라 준다.
  memo: 'success',
  other: 'default',
};

function categoryToKind(c: Category): UnifiedKind {
  if (c === 'journal') return 'visit';
  if (c === 'incidents') return 'incident';
  return 'schedule';
}

function normalizeSchedule(s: SiteSchedule): UnifiedItem {
  return {
    kind: 'schedule',
    id: s.id,
    at: s.scheduled_at ?? null,
    title: s.title,
    description: s.description,
    meta: EVENT_TYPE_LABEL[s.event_type],
    statusLabel: SCHEDULE_STATUS_LABEL[s.status],
    who: s.assignee,
    color: SCHEDULE_TYPE_COLOR[eventTypeFallback(s.event_type)],
    raw: s,
  };
}

function normalizeVisit(v: SiteVisitLog): UnifiedItem {
  return {
    kind: 'visit',
    id: v.id,
    at: v.visited_at,
    title: v.purpose,
    description: v.work_summary,
    meta: '출장',
    statusLabel: '기록',
    who: v.visitor_name,
    color: KIND_META.visit.color,
    raw: v,
  };
}

function normalizeIncident(i: SiteIncident): UnifiedItem {
  return {
    kind: 'incident',
    id: i.id,
    at: i.occurred_at,
    title: i.title,
    description: i.summary,
    meta: SEVERITY_LABEL[i.severity],
    statusLabel: INCIDENT_STATUS_LABEL[i.status],
    who: i.assignee,
    color: KIND_META.incident.color,
    raw: i,
  };
}

function formatDateTimeShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', {
    month: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const UnifiedScheduleSection: React.FC<Props> = ({ projectId, siteName, defaultCategory = 'schedules' }) => {
  // 일정/출장/장애 3종 조회는 공용 훅으로 통합 (SiteSchedulesPage 와 공유).
  const { schedules, visits, incidents, loading, error, reload } = useSiteTimelineData(
    projectId,
    '조회 실패',
  );

  const [sort, setSort] = useState<SortMode>('desc');
  // '일정' 메뉴는 통합 메인 진입점이라 전체 표시가 기본. 출장/장애 메뉴는
  // 해당 카테고리만 ON 으로 시작 — 어느 쪽이든 chip 으로 즉시 변경 가능.
  const [activeKinds, setActiveKinds] = useState<Set<UnifiedKind>>(() =>
    defaultCategory === 'schedules'
      ? new Set<UnifiedKind>(['schedule', 'visit', 'incident'])
      : new Set<UnifiedKind>([categoryToKind(defaultCategory)]),
  );

  /**
   * '내 것만' — 메인 대시보드 일정 캘린더의 "내 일정" 과 같은 기준(작성자).
   * 거기서는 서버 필터(mine=true)지만, 여기 3종 목록은 이미 현장 단위로 전부
   * 받아 오므로 클라이언트에서 거른다 — 엔드포인트를 늘릴 이유가 없다.
   */
  const [mineOnly, setMineOnly] = useState(false);
  const myUserId = useMemo(() => getUserInfo()?.user_id ?? null, []);

  const [scheduleDetailId, setScheduleDetailId] = useState<number | null>(null);
  const [visitDetailId, setVisitDetailId] = useState<number | null>(null);
  // 타임라인 항목 hover/focus 시 캘린더에서 강조할 날짜('YYYY-MM-DD').
  const [hoverIso, setHoverIso] = useState<string | null>(null);

  // 등록은 종류를 가리지 않는 단일 다이얼로그로 통일했다 — 어떤 종류로 열릴지만
  // 정하고, 실제 선택은 다이얼로그 최상단에서 한다.
  const [createOpen, setCreateOpen] = useState(false);
  const [createKind, setCreateKind] = useState<RecordKind>('schedule');
  // 캘린더 빈 날짜 클릭으로 등록 모달을 열 때 시작 일시 preset.
  const [presetStart, setPresetStart] = useState<Date | null>(null);
  // 캘린더에서 날짜를 이어 드래그했을 때의 종료 일시 preset.
  const [presetEnd, setPresetEnd] = useState<Date | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<SiteSchedule | null>(null);
  const [editingVisit, setEditingVisit] = useState<SiteVisitLog | null>(null);
  const writable = useMemo(() => canEditSchedule(), []);

  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'success' });


  const items = useMemo<UnifiedItem[]>(() => {
    return [
      ...schedules.map(normalizeSchedule),
      ...visits.map(normalizeVisit),
      ...incidents.map(normalizeIncident),
    ];
  }, [schedules, visits, incidents]);

  // 종류 chip 의 개수도 '내 것만' 을 반영해야 한다 — 3(전체) 이라 적혀 있는데
  // 1건만 보이면 필터가 아니라 버그로 읽힌다.
  const visibleItems = useMemo(
    () =>
      mineOnly && myUserId != null
        ? items.filter((it) => it.raw.created_by_id === myUserId)
        : items,
    [items, mineOnly, myUserId],
  );

  const counts = useMemo(() => {
    const c: Record<UnifiedKind, number> = { schedule: 0, visit: 0, incident: 0 };
    visibleItems.forEach((it) => {
      c[it.kind] += 1;
    });
    return c;
  }, [visibleItems]);

  const filteredSorted = useMemo(() => {
    const filtered = visibleItems.filter((it) => activeKinds.has(it.kind));
    // 시점 없는 항목(메모)은 정렬 방향과 무관하게 항상 끝으로 — 시간축에 자리가
    // 없는 항목을 시간순 목록 중간에 끼우면 순서가 뜻을 잃는다.
    const timeOf = (v: string | null): number | null => {
      if (!v) return null;
      const t = new Date(v).getTime();
      return Number.isNaN(t) ? null : t;
    };
    const byTime = (a: UnifiedItem, b: UnifiedItem): number => {
      const ta = timeOf(a.at);
      const tb = timeOf(b.at);
      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;
      return sort === 'asc' ? ta - tb : tb - ta;
    };
    const sorted = [...filtered].sort(byTime);
    if (sort === 'grouped') {
      // kind 우선, 같은 kind 안에서는 시간 desc.
      const order: Record<UnifiedKind, number> = { schedule: 0, visit: 1, incident: 2 };
      sorted.sort((a, b) => {
        const ka = order[a.kind];
        const kb = order[b.kind];
        if (ka !== kb) return ka - kb;
        // grouped 안에서도 시간 desc — byTime 은 sort 상태를 보므로 여기선
        // desc 를 명시적으로 다시 계산한다.
        const ta = timeOf(a.at);
        const tb = timeOf(b.at);
        if (ta === null && tb === null) return 0;
        if (ta === null) return 1;
        if (tb === null) return -1;
        return tb - ta;
      });
    }
    return sorted;
  }, [visibleItems, activeKinds, sort]);

  // 타임라인은 한 페이지 5건 — 업무가 쌓이면 리스트가 캘린더보다 길어져
  // 스크롤이 끝없이 늘어난다. 필터/정렬이 바뀌면 1페이지로 돌아간다.
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(filteredSorted.length / TIMELINE_PAGE_SIZE));
  useEffect(() => {
    setPage(1);
  }, [activeKinds, sort]);
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const pagedItems = useMemo(
    () =>
      filteredSorted.slice((page - 1) * TIMELINE_PAGE_SIZE, page * TIMELINE_PAGE_SIZE),
    [filteredSorted, page],
  );

  const calendarEvents = useMemo<CalendarEvent[]>(() => {
    // 날짜 없는 항목은 마커를 찍을 칸이 없다. isoOf(null) 은 epoch 를 거쳐
    // '1970-01-01' 이 되므로 걸러내지 않으면 그 날짜에 뭉쳐 붙는다.
    return visibleItems
      .filter((it) => !!it.at)
      .map((it) => ({
        id: `${it.kind}-${it.id}`,
        iso: isoOf(it.at as string),
        color: it.color === 'default' ? 'default' : (it.color as CalendarEvent['color']),
        label: it.title,
      }));
  }, [visibleItems]);

  const toggleKind = (k: UnifiedKind) => {
    setActiveKinds((prev) => {
      const next = new Set(prev);
      if (next.has(k)) {
        if (next.size > 1) next.delete(k);
      } else {
        next.add(k);
      }
      return next;
    });
  };

  const allKindsActive = activeKinds.size === 3;
  const toggleAll = () => {
    if (allKindsActive) {
      setActiveKinds(new Set(['schedule']));
    } else {
      setActiveKinds(new Set(['schedule', 'visit', 'incident']));
    }
  };

  const handleItemClick = (it: UnifiedItem) => {
    if (it.kind === 'schedule') setScheduleDetailId(it.id);
    else if (it.kind === 'visit') setVisitDetailId(it.id);
    else {
      // incident detail dialog 는 photos/comments 의존성이 있어 통합 페이지에서
      // mount 가 까다롭다 — phase 2 에서 처리. 일단 알림으로 안내.
      setSnack({
        open: true,
        message: '장애 이력 상세는 좌측 사이드바 "장애 이력" 메뉴에서 열람하세요.',
        severity: 'info',
      });
    }
  };

  // 캘린더 셀 안의 일정 라벨 클릭 → 타임라인 카드 클릭과 동일하게 상세 dialog.
  const handleCalendarEventClick = (e: CalendarEvent) => {
    const found = items.find((it) => `${it.kind}-${it.id}` === e.id);
    if (found) handleItemClick(found);
  };

  // 캘린더의 비어 있는 날짜 클릭 → 그 날짜(09:00)로 일정 등록 모달.
  // 일정이 있는 날짜는 셀 안의 라벨 클릭(상세 보기)이 주 동작이므로 그대로 둔다.
  const handleDaySelect = (iso: string | null) => {
    if (!iso || !writable) return;
    if (calendarEvents.some((e) => e.iso === iso)) return;
    setPresetStart(new Date(`${iso}T09:00:00`));
    setPresetEnd(null);
    setCreateKind('schedule');
    setCreateOpen(true);
  };

  // 캘린더에서 날짜를 이어 드래그 → 그 기간(첫날 09:00 ~ 마지막 날 18:00)으로
  // 시작·종료가 채워진 일정 등록 모달. 클릭과 달리 기존 일정 유무는 따지지
  // 않는다 — 여러 날을 훑으면 일정 있는 날이 끼는 게 보통이다.
  const handleRangeSelect = (startIso: string, endIso: string) => {
    if (!writable) return;
    setPresetStart(new Date(`${startIso}T09:00:00`));
    setPresetEnd(new Date(`${endIso}T18:00:00`));
    setCreateKind('schedule');
    setCreateOpen(true);
  };

  /**
   * 등록 다이얼로그 열기. 종류를 고르는 일은 다이얼로그가 하므로 여기서는
   * 처음 보일 종류만 정한다 (버튼 등록은 preset 없이 현재 시각 기본값).
   *
   * 장애 이력은 아직 폼이 IncidentListView 안에 inline 으로만 있어 이 진입점에
   * 넣지 못했다 — 사이드바 '장애 이력' 메뉴에서 등록한다.
   */
  const openCreate = (kind: RecordKind) => {
    setPresetStart(null);
    setPresetEnd(null);
    setCreateKind(kind);
    setCreateOpen(true);
  };

  const handleEditSchedule = (s: SiteSchedule) => {
    setScheduleDetailId(null);
    setEditingSchedule(s);
  };
  const handleEditVisit = (v: SiteVisitLog) => {
    setVisitDetailId(null);
    setEditingVisit(v);
  };

  const handleDeleteSchedule = async (s: SiteSchedule) => {
    if (!window.confirm(`'${s.title}' 일정을 삭제하시겠습니까?`)) return;
    try {
      await SiteScheduleService.delete(s.id);
      setScheduleDetailId(null);
      setSnack({ open: true, message: '일정이 삭제되었습니다', severity: 'success' });
      await reload();
    } catch (e) {
      setSnack({
        open: true,
        message: e instanceof Error ? e.message : '삭제 실패',
        severity: 'error',
      });
    }
  };
  const handleDeleteVisit = async (v: SiteVisitLog) => {
    if (!window.confirm(`'${v.purpose}' 출장 일지를 삭제하시겠습니까?`)) return;
    try {
      await SiteVisitLogService.delete(v.id);
      setVisitDetailId(null);
      setSnack({ open: true, message: '출장 일지가 삭제되었습니다', severity: 'success' });
      await reload();
    } catch (e) {
      setSnack({
        open: true,
        message: e instanceof Error ? e.message : '삭제 실패',
        severity: 'error',
      });
    }
  };

  const scheduleDetail = useMemo(
    () => (scheduleDetailId == null ? null : schedules.find((s) => s.id === scheduleDetailId) ?? null),
    [scheduleDetailId, schedules],
  );
  const visitDetail = useMemo(
    () => (visitDetailId == null ? null : visits.find((v) => v.id === visitDetailId) ?? null),
    [visitDetailId, visits],
  );

  return (
    <Box>
      {/* 헤더 — 제목 + 신규 등록 메뉴 */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}
      >
        <Stack direction="row" spacing={1.25} alignItems="baseline">
          <Typography variant="h6" fontWeight={700}>
            일정 관리
          </Typography>
          <Typography variant="caption" color="text.secondary">
            일정 · 출장 일지 · 장애 이력 통합 timeline
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="새로고침">
            <IconButton onClick={() => reload()} size="small" disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {/* 업무 / 파일 — 버튼이 곧 종류다. 다이얼로그 안에서 다시 고르게
              하지 않는다("무엇을 남기나요" 선택 단계 제거). */}
          {writable && (
            <>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => openCreate('schedule')}
              >
                업무 등록
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadFileIcon />}
                onClick={() => openCreate('file')}
              >
                파일 등록
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {/* 필터 / 정렬 도구 */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <Chip
          label={`전체 (${visibleItems.length})`}
          variant={allKindsActive ? 'filled' : 'outlined'}
          color={allKindsActive ? 'primary' : 'default'}
          onClick={toggleAll}
          size="small"
        />
        {(['schedule', 'visit', 'incident'] as UnifiedKind[]).map((k) => {
          const meta = KIND_META[k];
          const active = activeKinds.has(k);
          const Icon = meta.Icon;
          return (
            <Chip
              key={k}
              icon={<Icon fontSize="small" />}
              label={`${meta.label} (${counts[k]})`}
              variant={active ? 'filled' : 'outlined'}
              color={active ? meta.color : 'default'}
              onClick={() => toggleKind(k)}
              size="small"
            />
          );
        })}
        {/* 내 것만 — 작성자가 나인 항목. 메인 대시보드 캘린더의 "내 일정" 과 같은 기준. */}
        {myUserId != null && (
          <Chip
            label="내 것만"
            variant={mineOnly ? 'filled' : 'outlined'}
            color={mineOnly ? 'primary' : 'default'}
            onClick={() => setMineOnly((v) => !v)}
            size="small"
          />
        )}
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          value={sort}
          exclusive
          size="small"
          onChange={(_, v) => v && setSort(v as SortMode)}
        >
          <ToggleButton value="desc" sx={{ px: 1, py: 0.25, fontSize: '0.75rem' }}>
            <DescIcon fontSize="inherit" sx={{ mr: 0.25 }} /> 최신순
          </ToggleButton>
          <ToggleButton value="asc" sx={{ px: 1, py: 0.25, fontSize: '0.75rem' }}>
            <AscIcon fontSize="inherit" sx={{ mr: 0.25 }} /> 과거순
          </ToggleButton>
          <ToggleButton value="grouped" sx={{ px: 1, py: 0.25, fontSize: '0.75rem' }}>
            <GroupedIcon fontSize="inherit" sx={{ mr: 0.25 }} /> 유형별
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* 좌(timeline) / 우(캘린더) */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          // 타임라인 폭을 줄이고 캘린더를 크게 — 캘린더가 주(主), 타임라인이 보조.
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1.9fr)' },
          alignItems: 'start',
        }}
      >
        {/* 좌: timeline */}
        <Paper variant="outlined" sx={{ minHeight: 320, order: { xs: 2, md: 1 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : filteredSorted.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
              해당 조건의 항목이 없습니다.
            </Typography>
          ) : (
            <>
              <UnifiedTimeline
                items={pagedItems}
                sort={sort}
                onItemClick={handleItemClick}
                onHover={setHoverIso}
              />
              {totalPages > 1 && (
                <>
                  <Divider />
                  <Box sx={{ py: 1 }}>
                    <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
                  </Box>
                </>
              )}
            </>
          )}
        </Paper>

        {/* 우: 캘린더 */}
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 1,
            order: { xs: 1, md: 2 },
            position: { md: 'sticky' },
            top: { md: 16 },
          }}
        >
          {/* 빈 날짜 셀 클릭 → 해당 날짜로 일정 등록 모달(권한자만). 셀 안의 일정
              라벨 클릭은 해당 항목 상세 dialog 로 연결. lg 모드: 큰 셀 + 일정 제목
              라벨(메인 캘린더). */}
          <MiniCalendar
            events={calendarEvents}
            size="lg"
            selectedIso={null}
            onSelect={handleDaySelect}
            onEventClick={handleCalendarEventClick}
            onRangeSelect={writable ? handleRangeSelect : undefined}
            highlightIso={hoverIso}
          />
          <Divider sx={{ my: 1.25 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            범례
          </Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" rowGap={0.5}>
            {/* 일정 유형별 색 — '장애' 빨강은 장애 이력(kind)과 같은 의미라 공유. */}
            {(Object.keys(SCHEDULE_TYPE_COLOR) as ScheduleEventType[]).map((t) => (
              <LegendDot
                key={t}
                color={SCHEDULE_TYPE_COLOR[t] as CalendarEvent['color']}
                label={EVENT_TYPE_LABEL[t]}
              />
            ))}
            <LegendDot color={KIND_META.visit.color as CalendarEvent['color']} label="출장 일지" />
          </Stack>
        </Paper>
      </Box>

      {/* === Detail dialogs === */}
      <ScheduleDetailDialog
        open={scheduleDetail != null}
        schedule={scheduleDetail}
        onClose={() => setScheduleDetailId(null)}
        onEdit={writable ? handleEditSchedule : undefined}
        onDelete={writable ? handleDeleteSchedule : undefined}
      />
      <VisitLogDetailDialog
        open={visitDetail != null}
        log={visitDetail}
        onClose={() => setVisitDetailId(null)}
        onEdit={writable ? handleEditVisit : undefined}
        onDelete={writable ? handleDeleteVisit : undefined}
      />

      {/* === Create/Edit dialogs === */}
      {/* 출장 일지 **수정** 전용 — 등록은 통합 다이얼로그가 맡는다. 저장된
          일지를 다른 종류로 바꿀 수는 없으므로 종류 선택기를 노출하지 않는다. */}
      <VisitLogFormDialog
        open={editingVisit != null}
        projectId={projectId}
        siteName={siteName}
        editing={editingVisit}
        onClose={() => setEditingVisit(null)}
        onSaved={() => {
          setEditingVisit(null);
          setSnack({ open: true, message: '출장 일지가 저장되었습니다', severity: 'success' });
          void reload();
        }}
      />

      {/* 타임라인을 아래로 훑다가도 헤더로 올라가지 않고 등록할 수 있게 */}
      {writable && <QuickRecordFab onClick={() => openCreate('schedule')} label="업무 등록" />}

      {/* 종류는 버튼에서 이미 정해졌다 — kinds 를 하나로 좁히면 종류 선택기가
          뜨지 않는다. */}
      <RecordFormDialog
        open={createOpen}
        kinds={[createKind]}
        onClose={() => {
          setCreateOpen(false);
          setPresetStart(null);
          setPresetEnd(null);
        }}
        initialKind={createKind}
        projectId={projectId}
        siteName={siteName}
        presetStart={presetStart}
        presetEnd={presetEnd}
        onSaved={(r) => {
          setCreateOpen(false);
          setPresetStart(null);
          setPresetEnd(null);
          setSnack({
            open: true,
            message:
              r.kind === 'file'
                ? `파일 ${r.uploadedCount ?? 0}건이 올라갔습니다`
                : `${RECORD_KIND_META[r.kind].label}이(가) 등록되었습니다`,
            severity: 'success',
          });
          void reload();
        }}
      />

      {/* 일정 등록·편집 — 단일 공용 폼(상태·위치·다중 담당자 포함). 등록(editing=null)과
          편집을 같은 자리에서 처리. */}
      {/* 일정/메모 **수정** 전용 — 이 둘은 같은 테이블이라 폼 안에서 서로 전환된다. */}
      <ScheduleFormDialog
        open={editingSchedule != null}
        projectId={projectId}
        siteName={siteName}
        editing={editingSchedule}
        onClose={() => setEditingSchedule(null)}
        onSaved={(saved) => {
          setEditingSchedule(null);
          setSnack({
            open: true,
            message: `${saved.event_type === 'memo' ? '메모' : '일정'}가 수정되었습니다`,
            severity: 'success',
          });
          void reload();
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

interface UnifiedTimelineProps {
  items: UnifiedItem[];
  sort: SortMode;
  onItemClick: (it: UnifiedItem) => void;
  /** 항목 hover/focus 시 호출 — 캘린더 강조용 날짜 전달(해제 시 null). */
  onHover: (iso: string | null) => void;
}

const UnifiedTimeline: React.FC<UnifiedTimelineProps> = ({ items, sort, onItemClick, onHover }) => {
  if (sort !== 'grouped') {
    return (
      <Box>
        {items.map((it, idx) => (
          <TimelineRow
            key={`${it.kind}-${it.id}`}
            item={it}
            onClick={() => onItemClick(it)}
            onHover={onHover}
            divider={idx < items.length - 1}
          />
        ))}
      </Box>
    );
  }

  // grouped — kind 별 section.
  const groups: Record<UnifiedKind, UnifiedItem[]> = {
    schedule: [],
    visit: [],
    incident: [],
  };
  items.forEach((it) => groups[it.kind].push(it));
  return (
    <Box>
      {(['schedule', 'visit', 'incident'] as UnifiedKind[]).map((k) => {
        if (groups[k].length === 0) return null;
        const meta = KIND_META[k];
        const Icon = meta.Icon;
        return (
          <Box key={k} sx={{ '&:not(:last-child)': { borderBottom: '2px solid', borderColor: 'divider' } }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}
            >
              <Icon fontSize="small" color={meta.color === 'default' ? 'action' : meta.color} />
              <Typography variant="subtitle2" fontWeight={700}>
                {meta.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({groups[k].length}건)
              </Typography>
            </Stack>
            {groups[k].map((it, idx) => (
              <TimelineRow
                key={`${k}-${it.id}`}
                item={it}
                onClick={() => onItemClick(it)}
                onHover={onHover}
                divider={idx < groups[k].length - 1}
              />
            ))}
          </Box>
        );
      })}
    </Box>
  );
};

interface TimelineRowProps {
  item: UnifiedItem;
  onClick: () => void;
  onHover: (iso: string | null) => void;
  divider: boolean;
}

const TimelineRow: React.FC<TimelineRowProps> = ({ item, onClick, onHover, divider }) => {
  const meta = KIND_META[item.kind];
  const Icon = meta.Icon;
  // 날짜 없는 메모는 캘린더에 연동할 칸이 없다 — hover 하이라이트도 하지 않는다.
  const iso = item.at ? isoOf(item.at) : null;
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onMouseEnter={() => onHover(iso)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(iso)}
      onBlur={() => onHover(null)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: 'grid',
        gridTemplateColumns: '4px minmax(0, 1fr)',
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
        borderBottom: divider ? '1px solid' : 'none',
        borderColor: 'divider',
      }}
    >
      {/* 좌측 strip — kind/색 표시 */}
      <Box
        sx={{
          bgcolor: (theme) =>
            item.color === 'default'
              ? theme.palette.grey[400]
              : theme.palette[item.color as 'primary'].main,
        }}
      />
      <Box sx={{ px: 2, py: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5, mb: 0.5 }}>
          <Icon fontSize="inherit" sx={{ color: 'text.secondary', fontSize: '0.95rem' }} />
          <Chip
            label={meta.label}
            size="small"
            color={meta.color === 'default' ? 'default' : meta.color}
            variant="outlined"
            sx={{ height: 18, fontSize: '0.65rem' }}
          />
          {item.meta && (
            <Chip
              label={item.meta}
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          )}
          {item.statusLabel && (
            <Chip
              label={item.statusLabel}
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: 'auto', fontStyle: item.at ? 'normal' : 'italic' }}
          >
            {item.at ? formatDateTimeShort(item.at) : '날짜 없음'}
          </Typography>
        </Stack>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
          {item.title}
        </Typography>
        {item.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.5,
              mb: 0.5,
            }}
          >
            {markdownToPlainText(item.description)}
          </Typography>
        )}
        {item.who && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <PersonIcon fontSize="inherit" sx={{ color: 'text.secondary', fontSize: '0.85rem' }} />
            <Typography variant="caption" color="text.secondary">
              {item.who}
            </Typography>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

const LegendDot: React.FC<{ color: CalendarEvent['color']; label: string }> = ({ color, label }) => {
  const colorMap: Record<string, string> = {
    primary: '#1976d2',
    info: '#0288d1',
    error: '#d32f2f',
    warning: '#ed6c02',
    success: '#2e7d32',
    secondary: '#9c27b0',
    default: '#9e9e9e',
  };
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: colorMap[color ?? 'primary'] }} />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
};

export default UnifiedScheduleSection;
